import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import styles from './image-upload.module.scss';
import baseStyles from '../styles/App.module.scss';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { uploadImage } from '../api';

interface ImageUploadProps {
    onImageSelect?: (imageFile: File | null) => void;
    onImageUploaded?: (imageId: string) => void;
    maxFileSize?: number; // in MB
    aspectRatio?: number; // width/height ratio
    className?: string;
    initialImageId?: string; // For editing existing images
}

export interface ImageUploadRef {
    cropAndUpload: () => Promise<void>;
    hasImage: () => boolean;
    isUploading: () => boolean;
}


const ImageUpload = forwardRef<ImageUploadRef, ImageUploadProps>(({
    onImageSelect,
    onImageUploaded,
    maxFileSize = 12,
    className,
    initialImageId
}, ref) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState<string>('');
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [showCrop, setShowCrop] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedImageId, setUploadedImageId] = useState<string | null>(initialImageId || null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    useImperativeHandle(ref, () => ({
        cropAndUpload: handleCropConfirm,
        hasImage: () => !!selectedFile,
        isUploading: () => isUploading
    }));

    const validateFile = useCallback((file: File): boolean => {
        const maxSizeBytes = maxFileSize * 1024 * 1024;

        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file.');
            return false;
        }

        if (file.size > maxSizeBytes) {
            setError(`File size must be less than ${maxFileSize}MB.`);
            return false;
        }

        setError('');
        return true;
    }, [maxFileSize]);

    const getCroppedImg = useCallback((
        image: HTMLImageElement,
        pixelCrop: PixelCrop,
        fileName: string
    ): Promise<File> => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('No 2d context');
        }

        // Get the actual image dimensions (not the displayed dimensions)
        const { naturalWidth, naturalHeight } = image;
        const { width: displayWidth, height: displayHeight } = image;

        // Calculate scale factors
        const scaleX = naturalWidth / displayWidth;
        const scaleY = naturalHeight / displayHeight;

        // Convert display crop coordinates to natural image coordinates
        const sourceX = pixelCrop.x * scaleX;
        const sourceY = pixelCrop.y * scaleY;
        const sourceWidth = pixelCrop.width * scaleX;
        const sourceHeight = pixelCrop.height * scaleY;

        // Set canvas size to the crop dimensions
        canvas.width = sourceWidth;
        canvas.height = sourceHeight;

        // Draw the cropped portion of the image
        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            sourceWidth,
            sourceHeight
        );

        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                const file = new File([blob], fileName, { type: blob.type });
                resolve(file);
            }, 'image/jpeg', 0.9);
        });
    }, []);

    const handleCropComplete = useCallback((crop: Crop, percentageCrop: Crop) => {
        // Convert percentage crop to pixel crop
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const pixelCrop: PixelCrop = {
                x: (percentageCrop.x / 100) * width,
                y: (percentageCrop.y / 100) * height,
                width: (percentageCrop.width / 100) * width,
                height: (percentageCrop.height / 100) * height,
                unit: 'px'
            };
            setCompletedCrop(pixelCrop);
        }
    }, []);

    const handleCropChange = useCallback((crop: Crop, percentageCrop: Crop) => {
        setCrop(percentageCrop);
    }, []);

    const handleCropConfirm = useCallback(async () => {
        if (!completedCrop || !imgRef.current || !selectedFile) {
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            const croppedImageFile = await getCroppedImg(
                imgRef.current,
                completedCrop,
                selectedFile.name
            );

            // Upload the cropped image
            const uploadResult = await uploadImage(croppedImageFile);

            setSelectedFile(croppedImageFile);
            setUploadedImageId(uploadResult.imageId);

            // Create a synthetic event for Formik handleChange
            if (onImageSelect) {
                const syntheticEvent = {
                    target: {
                        name: 'imageId',
                        value: croppedImageFile
                    },
                    persist: () => { }
                };
                onImageSelect(syntheticEvent as any);
            }

            // Call the upload callback with the image ID and filename
            if (onImageUploaded) {
                onImageUploaded(uploadResult.imageId);
            }

            setShowCrop(false);
        } catch (error) {
            console.error('Upload failed:', error);
            setError('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
        }
    }, [completedCrop, selectedFile, getCroppedImg, onImageSelect, onImageUploaded]);

    const handleFileSelect = useCallback((file: File) => {
        if (!validateFile(file)) {
            return;
        }

        setSelectedFile(file);
        setShowCrop(true);

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result as string;
            setPreviewUrl(result);

            // Initialize crop with 16:9 aspect ratio
            const img = new Image();
            img.onload = () => {
                const { width, height } = img;
                const aspectRatio = 16 / 9;

                let cropWidth, cropHeight, cropX, cropY;

                if (width / height > aspectRatio) {
                    // Image is wider than 16:9, crop width
                    cropHeight = 100; // Use full height
                    cropWidth = (100 * aspectRatio * height) / width;
                    cropX = (100 - cropWidth) / 2;
                    cropY = 0;
                } else {
                    // Image is taller than 16:9, crop height
                    cropWidth = 100; // Use full width
                    cropHeight = (100 * width) / (aspectRatio * height);
                    cropX = 0;
                    cropY = (100 - cropHeight) / 2;
                }

                const initialCrop: Crop = {
                    unit: '%',
                    x: cropX,
                    y: cropY,
                    width: cropWidth,
                    height: cropHeight
                };

                setCrop(initialCrop);
            };
            img.src = result;
        };
        reader.readAsDataURL(file);
    }, [validateFile]);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);

        const file = event.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const resetUpload = () => {
        setSelectedFile(null);
        setPreviewUrl('');
        setError('');
        setCrop(undefined);
        setCompletedCrop(undefined);
        setShowCrop(false);
        setIsUploading(false);
        setUploadedImageId(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Create a synthetic event for Formik handleChange
        if (onImageSelect) {
            const syntheticEvent = {
                target: {
                    name: 'imageId',
                    value: null
                },
                persist: () => { }
            };
            onImageSelect(syntheticEvent as any);
        }
    };



    const renderUploadArea = () => (
        <div
            className={classNames(
                styles.uploadArea,
                {
                    [styles.uploadAreaDragging]: isDragging,
                    [styles.uploadAreaError]: error
                },
                className
            )}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            <div className={styles.uploadContent}>
                <span className="material-icons">cloud_upload</span>
                <p className={styles.uploadText}>
                    {isDragging ? 'Drop image here' : 'Click or drag to upload image'}
                </p>
                <p className={styles.uploadSubtext}>
                    Maximum file size: {maxFileSize}MB
                </p>
            </div>
        </div>
    );

    const renderCropInterface = () => (
        <div className={styles.cropContainer}>
            <div className={styles.cropHeader}>
                <h3>Crop Image (16:9 ratio)</h3>
                <button
                    className={styles.cancelButton}
                    onClick={resetUpload}
                >
                    Cancel
                </button>
            </div>

            <div className={styles.cropArea}>
                <ReactCrop
                    crop={crop}
                    onChange={handleCropChange}
                    onComplete={handleCropComplete}
                    aspect={16 / 9}
                    minWidth={100}
                    minHeight={56}
                >
                    <img
                        ref={imgRef}
                        src={previewUrl}
                        alt="Crop preview"
                        className={styles.cropImage}
                    />
                </ReactCrop>
            </div>

        </div>
    );



    const renderSuccessMessage = () => (
        <div className={styles.successMessage}>
            <span className="material-icons">check_circle</span>
            <p>{initialImageId ? 'Existing image' : 'Image uploaded successfully!'}</p>
            <button
                className={styles.resetButton}
                onClick={resetUpload}
            >
                {initialImageId ? 'Change Image' : 'Upload Another Image'}
            </button>
        </div>
    );

    return (
        <div className={styles.imageUpload}>
            {error && (
                <div className={classNames(styles.errorMessage, baseStyles.errorColor)}>
                    {error}
                </div>
            )}

            {uploadedImageId && !showCrop ? renderSuccessMessage() :
                showCrop ? renderCropInterface() : renderUploadArea()}
        </div>
    );
});

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
